/**
 * HeatMap — DGUI component for 2D heat map (matrix with color scale).
 */

import { Card, CardContent, Typography, Box, Tooltip as MuiTooltip } from '@mui/material';
import type { HeatMapDGUIProps } from '@flowrithm/shared/src/features/ai-assist/types/dgui';

const cardSx = {
  borderRadius: 2,
  borderColor: 'rgba(4, 55, 123, 0.2)',
  backgroundColor: 'rgba(255, 255, 255, 0.9)',
  mt: 1,
  mb: 1,
  overflow: 'auto',
};

function formatValue(
  value: number,
  format?: 'number' | 'percent' | 'currency',
  currencyCode?: string
): string {
  if (format === 'percent') return `${value}%`;
  if (format === 'currency' && currencyCode) {
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: currencyCode,
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      }).format(value);
    } catch {
      return `${currencyCode} ${value}`;
    }
  }
  return String(value);
}

/** Interpolate value in [min, max] to a 0–1 factor for color. */
function toFactor(value: number, min: number, max: number): number {
  if (max === min) return 0.5;
  return Math.max(0, Math.min(1, (value - min) / (max - min)));
}

/** Sequential: single hue (blue) low→high. */
function sequentialColor(factor: number): string {
  const opacity = 0.2 + factor * 0.8;
  return `rgba(4, 55, 123, ${opacity})`;
}

/** Diverging: red (low) → white (mid) → blue (high). */
function divergingColor(factor: number): string {
  if (factor <= 0.5) {
    const t = factor * 2; // 0..1
    return `rgb(255, ${Math.round(255 * (1 - t))}, ${Math.round(255 * (1 - t))})`;
  }
  const t = (factor - 0.5) * 2;
  return `rgb(${Math.round(255 * (1 - t))}, ${Math.round(255 * (1 - t))}, 255)`;
}

/** Neutral: grey scale. */
function neutralColor(factor: number): string {
  const g = Math.round(200 + factor * 55);
  return `rgb(${g},${g},${g})`;
}

function normalizeHeatMapProps(props: HeatMapDGUIProps & Record<string, unknown>): HeatMapDGUIProps {
  return {
    ...props,
    rowLabels: (props.rowLabels ?? props.row_labels) as string[],
    columnLabels: (props.columnLabels ?? props.column_labels) as string[],
    valueFormat: (props.valueFormat ?? props.value_format) as HeatMapDGUIProps['valueFormat'],
    currencyCode: props.currencyCode ?? (props.currency_code as string),
    colorScale: (props.colorScale ?? props.color_scale) as HeatMapDGUIProps['colorScale'],
    valueMin: props.valueMin ?? (props.value_min as number),
    valueMax: props.valueMax ?? (props.value_max as number),
    showCellValues: props.showCellValues ?? (props.show_cell_values as boolean),
  };
}

export function HeatMap(initialProps: HeatMapDGUIProps & Record<string, unknown>) {
  const {
    title,
    subtitle,
    rowLabels = [],
    columnLabels = [],
    data = [],
    valueFormat = 'number',
    currencyCode,
    colorScale = 'sequential',
    valueMin,
    valueMax,
    showCellValues = false,
  } = normalizeHeatMapProps(initialProps);
  const safeRows = Array.isArray(rowLabels) ? rowLabels : [];
  const safeCols = Array.isArray(columnLabels) ? columnLabels : [];
  const safeData = Array.isArray(data) ? data : [];
  const numRows = safeRows.length;
  const numCols = safeCols.length;
  const isEmpty =
    numRows === 0 ||
    numCols === 0 ||
    safeData.length === 0 ||
    (safeData[0] && Array.isArray(safeData[0]) && (safeData[0] as (number | null)[]).length === 0);

  const values: number[] = [];
  for (let r = 0; r < safeData.length; r++) {
    const row = safeData[r];
    if (!Array.isArray(row)) continue;
    for (let c = 0; c < row.length; c++) {
      const v = row[c];
      if (typeof v === 'number' && !Number.isNaN(v)) values.push(v);
    }
  }
  const min = valueMin ?? (values.length ? Math.min(...values) : 0);
  const max = valueMax ?? (values.length ? Math.max(...values) : 1);
  const getColor =
    colorScale === 'diverging'
      ? (v: number) => divergingColor(toFactor(v, min, max))
      : colorScale === 'neutral'
        ? (v: number) => neutralColor(toFactor(v, min, max))
        : (v: number) => sequentialColor(toFactor(v, min, max));

  return (
    <Card variant="outlined" sx={cardSx}>
      <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
        {title && (
          <Typography variant="subtitle1" fontWeight={600} color="#04377b" gutterBottom>
            {title}
          </Typography>
        )}
        {subtitle && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {subtitle}
          </Typography>
        )}
        {isEmpty ? (
          <Typography variant="body2" color="text.secondary">
            No data
          </Typography>
        ) : (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: `auto repeat(${numCols}, minmax(32px, 1fr))`,
              gap: 0.5,
              alignItems: 'center',
              justifyItems: 'center',
              fontSize: '0.75rem',
            }}
          >
            <Box />
            {safeCols.map((col, c) => (
              <Typography key={c} variant="caption" fontWeight={600} color="#04377b" sx={{ textAlign: 'center' }}>
                {col}
              </Typography>
            ))}
            {safeRows.map((rowLabel, r) => (
              <Box key={r} sx={{ display: 'contents' }}>
                <Typography variant="caption" color="text.secondary" sx={{ justifySelf: 'end' }}>
                  {rowLabel}
                </Typography>
                {Array.from({ length: numCols }, (_, c) => {
                  const row = Array.isArray(safeData[r]) ? (safeData[r] as (number | null)[]) : [];
                  const val = row[c] ?? null;
                  const num = typeof val === 'number' && !Number.isNaN(val) ? val : null;
                  const bg = num !== null ? getColor(num) : 'rgba(0,0,0,0.06)';
                  const cell = (
                    <Box
                      key={c}
                      sx={{
                        width: '100%',
                        minWidth: 32,
                        minHeight: 28,
                        backgroundColor: bg,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: 0.5,
                        color: num !== null && colorScale === 'sequential' ? (toFactor(num, min, max) > 0.6 ? '#fff' : '#333') : 'inherit',
                      }}
                    >
                      {showCellValues && num !== null ? formatValue(num, valueFormat, currencyCode) : null}
                    </Box>
                  );
                  return (
                    <MuiTooltip
                      key={c}
                      title={
                        num !== null
                          ? `${rowLabel} × ${safeCols[c]}: ${formatValue(num, valueFormat, currencyCode)}`
                          : '—'
                      }
                    >
                      {cell}
                    </MuiTooltip>
                  );
                })}
              </Box>
            ))}
          </Box>
        )}
      </CardContent>
    </Card>
  );
}
